import React, { useState } from "react";
import { IconButton, Tooltip, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import "./ImageEditor.scss";
import {
  APP_CLOUDINARY_CLOUDNAME,
  APP_CLOUDINARY_UPLOAD_PRESET,
  APP_CLOUDINARY_URL_ENDPOINT,
} from "../../../../../constants/api_urls";
import { Helmet } from "react-helmet";
import {
  CLOUDINARY_IMAGE_CATEGORIES,
  DEFAULT_RATIOS,
} from "../../../constants/cloudinaryImageCategory";
import {
  generateCroppedUrl,
  removeCropParameters,
} from "../../Utils/imageTools/imageCropper";
import { validateImage } from "../../Utils/validators/imageValidators";
import { InfoOutlined } from "@mui/icons-material";

const getFinalCloudinaryURL = (croppedUrl) => {
  const finalCroppedURL = `https://res.cloudinary.com/${APP_CLOUDINARY_CLOUDNAME}/image/fetch/c_scale,h_400/f_jpg/${croppedUrl}`;
  return finalCroppedURL;
}

const CloudinaryImageEditor = ({
                                 landscape,
                                 setLandscape,
                                 logo,
                                 setLogo,
                                 errors = {},
                               }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const openUploadWidget = (category) => {
    const defaultRatio = DEFAULT_RATIOS[category];
    if (!defaultRatio) return;

    window.cloudinary.openUploadWidget(
        {
          cloudName: APP_CLOUDINARY_CLOUDNAME,
          uploadPreset: APP_CLOUDINARY_UPLOAD_PRESET,
          multiple: true, // ✅ Allow multiple file uploads
          sources: ["local", "url"],
          cropping: false, // ❌ Disable manual cropping UI
          croppingAspectRatio: defaultRatio, // ✅ Still sets default ratio internally
          showSkipCropButton: false,
          transformation: [
            {
              aspect_ratio: `${Math.round(defaultRatio * 100)}:${100}`,
              crop: "fill",
            },
          ],
        },
        async (error, result) => {
          if (!error && result?.event === "success") {
            const croppedUrl = generateCroppedUrl(result.info);
            await Promise.all([
              (async () => {

                updateImageCategory(category, (croppedUrl));
              })(),
              (async () => {
                const validationResult = await validateImage(
                    category,
                    result.info.secure_url
                );
                setValidationErrors((prev) => ({
                  ...prev,
                  [croppedUrl]: validationResult.success
                      ? null
                      : validationResult.error,
                }));
              })(),
            ]);
          }
        }
    );
  };

  const updateImageCategory = (category, url) => {
    switch (category) {
      case "images":
        setLandscape((prev) => [...prev, url]);
        break;
      case "logo":
        setLogo((prev) => [...prev, url]);
        break;
      default:
        break;
    }
  };

  const handleDelete = (index, category) => {
    const categoryArray = getCategoryArray(category);
    if (!categoryArray) return;

    const updatedArray = categoryArray.filter((_, i) => i !== index);
    setCategoryArray(category, updatedArray);
  };

  const getCategoryArray = (category) => {
    switch (category) {
      case "images":
        return landscape;
      case "logo":
        return logo;

      default:
        return null;
    }
  };

  const setCategoryArray = (category, updatedArray) => {
    switch (category) {
      case "images":
        setLandscape(updatedArray);
        break;
      case "logo":
        setLogo(updatedArray);
        break;
      default:
        break;
    }
  };

  const formatCategoryKey = (key) => {
    return key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^[a-z]/, (char) => char.toUpperCase());
  };

  const renderCategory = (categoryKey) => {
    const categoryArray = getCategoryArray(categoryKey);
    if (!categoryArray) return null;

    return categoryArray.map((url, index) => {
      const errorURL = String(url).replace(`https://res.cloudinary.com/${APP_CLOUDINARY_CLOUDNAME}/image/fetch/c_scale,h_400/f_jpg/`,'')

      const errorMessage =
          validationErrors[errorURL] ||
          null;

      const isHovered = hoveredIndex === index;
      return (
          <div
              className={`image-card ${errorMessage != null ? "error" : ""}`}
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
          >
            <img src={getFinalCloudinaryURL(url)} alt="Uploaded" />
            {(isHovered || errorMessage != null) && (
                <div
                    className={`image-overlay ${
                        errorMessage != null ? "has-error" : ""
                    }`}
                >
                  {errorMessage && <p className="error-message">{errorMessage}</p>}
                  <div className="image-actions">
                    <IconButton
                        color="primary"
                        onClick={() => setFullScreenImage(url)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                        color="secondary"
                        className="delete-icon"
                        onClick={() => handleDelete(index, categoryKey)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                </div>
            )}
          </div>
      );
    });
  };

  return (
    <div className="display-ads-cloudinary-image-editor">
      <Helmet>
        <script src={APP_CLOUDINARY_URL_ENDPOINT} type="text/javascript" />
      </Helmet>

      <div className="image-grid">
        {CLOUDINARY_IMAGE_CATEGORIES.map((category) => (
          <div className="cloudinary-category" key={category}>
            <div className="category-header">
              <div className="heading-text">
                {formatCategoryKey(category).toUpperCase()}
                {category === "images" && (
                  <Tooltip
                    title={"Image size: 10 MB and at least 1200x700 ֣px"}
                    placement="right-end"
                  >
                    {" "}
                    <InfoOutlined sx={{ fontSize: "14px" }} />
                  </Tooltip>
                )}
                {category === "logo" && (
                  <Tooltip
                    title={
                      "Adobe Illustrator (.ai) or .svg files are preferred for highest quality. jpg, or .png also work"
                    }
                    placement="right-end"
                  >
                    {" "}
                    <InfoOutlined sx={{ fontSize: "14px" }} />
                  </Tooltip>
                )}
                {(category === "images"
                  ? getCategoryArray("images").length < 3
                  : !getCategoryArray(category).length) &&
                    typeof errors[`${category}Image`] === "string" && (
                      <Typography className="text-error-section">
                        {errors[`${category}Image`]}
                      </Typography>
                    )}
              </div>
              <div className="button-add">
                <div className="divider" />
                <div
                  className="add-image-btn"
                  onClick={() => openUploadWidget(category)}
                >
                  <AddCircleOutlineIcon className="add-icon" />
                  {formatCategoryKey(category).toUpperCase()}
                </div>
              </div>
            </div>
            <div className="image-category">{renderCategory(category)}</div>
          </div>
        ))}
      </div>

      {fullScreenImage && (
        <div
          className="full-screen-overlay"
          onClick={() => setFullScreenImage(null)}
        >
          <img
            src={getFinalCloudinaryURL(removeCropParameters(fullScreenImage))}
            alt="Full Screen"
            className="full-screen-image"
          />
        </div>
      )}
    </div>
  );
};

export default CloudinaryImageEditor;
